import moment from "moment-timezone";
import { createStore, ActionTree, MutationTree } from "vuex";
import { cloneDeep } from "lodash";
import createPersistedState from "vuex-persistedstate";
import { NotificationManager } from "../services/notifications/notificationManager";
import { ProfileConfiguration } from "../../../backend/src/common/profile/profile";
import { ProfileType, UserType } from "../../../types/index";
import { AccountableAccountTable } from "../types/AccountableAccountTable";
import { Offer_type } from "../types/offer.types";
import {
    VatItemAssist,
    VatItemAuto,
} from "../../../backend/src/common/declaration/declarationServices";
import { PreviousVatCredit } from "../../../backend/src/common/types/taxformTypes";

const apmRoutes = [
    "openPoints",
    "outstandingTPC",
    "outstandingUser",
    "generalLedger",
    "auditReport",
    "auditReportToDocx",
    "exportAccounting",
    "accountingPeriodOpening",
    "budget-form-accountable-account",
    "accountingLog",
    "accountingLines",
    "dashboardAccounting",
    "declaration-new",
    "declaration-test",
    "taxForm2-new",
    "taxForm2",
    "declaration2-new",
    "declaration2",
    "declaration",
    "vatDeclarationAuto",
    "taxForms",
    "vatCalculationAssist",
    "fixedAssets",
    "taxFormsTab",
    "dasTwo",
];

const defaultDocumentFilter = {
    search: "",
    type: "ALL",
    allTypes: [],
    status: "ALL",
    declaredStatus: "ALL",
    filterByPrice: false,
    thirdPartyCompanyName: "",
    // providerId: undefined,
    tags: [],
    amount: {
        maximum: "",
        minimum: "",
    },
    date: {
        begin: "",
        end: "",
        format: "YYYY-MM-DD",
    },
    auditCheck: "",
    partitionTagId: "",
    pagination: {
        sortBy: ["date"],
        descending: [true],
        page: 1,
        rowsPerPage: 10,
        totalItems: 0,
    },
    prevNext: {
        currentId: "",
        prevId: "",
        nextId: "",
    },
    switchToNextNoValidatedDocumentAfterValidation: false,
};

const defaultBankTransactionFilter = {
    selectedAccount: "ALL",
    search: "",
    tags: [],
    // showFilterBox: false,
    // showNonReconcilied: false,
    reconciliationStatus: "ALL",
    amount: {
        type: "ALL",
        maximum: "",
        minimum: "",
    },
    date: {
        begin: "",
        end: "",
        format: "YYYY-MM-DD",
    },
    auditCheck: "",
    partitionTagId: "",
    pagination: {
        sortBy: ["dateDebit"],
        descending: [true],
        page: 1,
        rowsPerPage: 10,
        totalItems: 0,
    },
    payBy: "ALL",
    prevNext: {
        currentId: "",
        prevId: "",
        nextId: "",
    },
    displayGraph: false,
};

const defaultAccountingPeriod = {
    accountingPeriods: [],
    selectedAccountingPeriod: null,
    lastSelectedAccountingPeriod: null,
    accountingPeriodWasNull: true,
    isMandatory: false,
    readonly: false,
    selectedAccountingClosing: null,
    lastSelectedAccountingClosing: null,
    accountingSituations: [],
    isAccountingSituationOf: null,
};

const defaultTag = {
    expandPartitions: false,
};

type Profile = {
    id: string;
    name: string;
    displayName: string;
    type: ProfileType;
    restrictionLevel: number;
    configuration: ProfileConfiguration;
    homePage: string;
};
// type AccountableAccount = {
//     id: string
//     name: string
//     displayName: string
//     description: string
//     shortDescription: string
//     subAccountableAccountOf
//     tag
// }
// type AccountableAccountTable = {
//     id: string
//     name: string
//     accountableAccounts:AccountableAccount []
// }

export class User {
    id: string = "";
    type: UserType = "";
    email: string = "";
    phoneNumber: string = "";
    firstLogin: boolean = false;
    firstname: string = "";
    marriedName: string = "";
    lastname: string = "";
    emailVerified: boolean = false;
    lastAppVersion: string = "";
    allAvailableCompanies = [];
    profile: Profile = null;
    companyUserOriginalProfile: Profile = null;
    switchOriginalProfile: Profile = null;
    companyUser: any = null;
    company: any = { id: "", name: "" };
    selectedCompany: any = {
        id: "",
        name: "",
        profile: { managementAuthorization: false, managementAuthorizations: null },
    };
    isSubUserOf: any = null;
    subUsers: any = [];

    constructor() {}

    get isAccountingUser(): boolean {
        if (!this.id || !this.profile) {
            return false;
        }

        if (this.profile?.configuration.accounting.read) return true;
    }

    get isAutonomousUser(): boolean {
        return this.profile?.name?.startsWith("customer-autonomous");
    }

    get isRoot(): boolean {
        return this.profile?.type === "ROOT";
    }

    get isInAccountingFirm(): boolean {
        return this.profile?.type === "ACCOUNTING";
    }

    get isAccountingClient(): boolean {
        return this.profile?.type === "ACCOUNTING_CLIENT";
    }

    get isBusinessClient(): boolean {
        // debugger;
        return this.profile?.type === "BUSINESS";
    }

    get isAccountingEmployee() {
        return this.profile.type === "ACCOUNTING" && this.profile.restrictionLevel >= 3;
    }

    get isConnected(): boolean {
        return !!this.id;
    }

    get headerUser() {
        const user = {
            // firstname: this.firstname,
            // lastname: this.lastname,
            profile: undefined,
        };
        if (this.profile) {
            user.profile = {
                type: this.profile.type,
                restrictionLevel: this.profile.restrictionLevel,
                name: this.profile.name,
                displayName: this.profile.displayName,
            };
        }
        return user;
    }

    // Erwann migrated with checkAccessRights
    // enableRead(type) {
    //     if (!type) {
    //         return false;
    //     }
    //     return this.profile?.configuration?.[type]?.read;
    // }

    // enableWrite(type) {
    //     if (!type) {
    //         return false;
    //     }
    //     return this.profile?.configuration?.[type]?.write;
    // }

    get userConfig() {
        return this.profile?.configuration;
    }

    get showOutStandingClient() {
        // debugger
        // if (this.isAccountingUser && (!this.company || !this.selectedCompany || this.company.id !== this.selectedCompany.id)) {
        //     return false
        // }
        if (!this.userConfig?.outstanding?.read) {
            return false;
        }
        if (
            this.selectedCompany?.profile?.managementAuthorization &&
            (!this.selectedCompany?.profile?.managementAuthorizations ||
                this.selectedCompany?.profile?.managementAuthorizations.client)
        ) {
            return true;
        }

        if (!this.companyUser?.configuration?.outstanding) {
            return false;
        }
        const outstanding = this.companyUser.configuration.outstanding;
        if (!outstanding.selectionClient && !outstanding.client) {
            return false;
        }

        return true;
    }

    get showOutStandingSupplier() {
        // debugger
        // if (this.isAccountingUser && (!this.company || !this.selectedCompany || this.company.id !== this.selectedCompany.id)) {
        //     return false
        // }
        if (!this.userConfig?.outstanding?.read) {
            return false;
        }
        if (
            this.selectedCompany?.profile?.managementAuthorization &&
            (!this.selectedCompany?.profile?.managementAuthorizations ||
                this.selectedCompany?.profile?.managementAuthorizations.supplier)
        ) {
            return true;
        }

        if (!this.companyUser?.configuration?.outstanding) {
            return false;
        }
        const outstanding = this.companyUser.configuration.outstanding;
        if (!outstanding.supplier) {
            return false;
        }

        return true;
    }

    get showOutStandingUser() {
        // debugger
        // if (this.isAccountingUser && (!this.company || !this.selectedCompany || this.company.id !== this.selectedCompany.id)) {
        //     return false
        // }
        if (!this.userConfig?.outstanding?.read) {
            return false;
        }
        if (
            this.selectedCompany?.profile?.managementAuthorization &&
            (!this.selectedCompany?.profile?.managementAuthorizations ||
                (this.selectedCompany?.profile?.managementAuthorizations.salary &&
                    this.selectedCompany?.profile?.managementAuthorizations.expenseReport))
        ) {
            return true;
        }

        if (!this.companyUser?.configuration?.outstanding) {
            return false;
        }
        const outstanding = this.companyUser.configuration.outstanding;
        if (!outstanding.expenseReport && !outstanding.salary) {
            return false;
        }

        return true;
    }

    get showOutStandingSalary() {
        // debugger
        // if (this.isAccountingUser && (!this.company || !this.selectedCompany || this.company.id !== this.selectedCompany.id)) {
        //     return false
        // }
        if (!this.userConfig?.outstanding?.read) {
            return false;
        }
        if (
            this.selectedCompany?.profile?.managementAuthorization &&
            (!this.selectedCompany?.profile?.managementAuthorizations ||
                this.selectedCompany?.profile?.managementAuthorizations.salary)
        ) {
            return true;
        }

        if (!this.companyUser?.configuration?.outstanding) {
            return false;
        }
        const outstanding = this.companyUser.configuration.outstanding;
        if (!outstanding.expenseReport && !outstanding.salary) {
            return false;
        }

        return true;
    }

    get showOutStandingExpenseReport() {
        // debugger
        // if (this.isAccountingUser && (!this.company || !this.selectedCompany || this.company.id !== this.selectedCompany.id)) {
        //     return false
        // }
        if (!this.userConfig?.outstanding?.read) {
            return false;
        }
        if (
            this.selectedCompany?.profile?.managementAuthorization &&
            (!this.selectedCompany?.profile?.managementAuthorizations ||
                this.selectedCompany?.profile?.managementAuthorizations.expenseReport)
        ) {
            return true;
        }

        if (!this.companyUser?.configuration?.outstanding) {
            return false;
        }
        const outstanding = this.companyUser.configuration.outstanding;
        if (!outstanding.expenseReport && !outstanding.salary) {
            return false;
        }

        return true;
    }
}

export type MailTemplate = {
    title: string;
    value: string;
    header: string;
    body: string;
    signature: string;
};

export interface RootState {
    test: boolean;
    inAdmin: boolean;
    offerType: Offer_type;
    version: string;
    maintenance: boolean;
    currentRoute: string;
    user: User;
    subUserId: string;
    // accountingFirmId:string,
    selected_company: any;
    lastSelected_company: any;
    companyAccountingFirm: any;
    accountingFirmClient: any;
    documentFilter: any;
    companyFilter: any;
    companyEdition: any;
    bankTransactionFilter: any;
    accountingLineFilter: any;
    expenseReportsFilter: any;
    ztillReceiptsFilter: any;
    userFilter: any;
    invoiceFilter: any;
    stockFilter: any;
    auditFilter: any;
    toBeDoneStatus: boolean;
    activateHelp: boolean;
    firstHelpDone: boolean;
    invoice: any;
    stock: any;
    apolloErrors: any;
    generalLedger: any;
    accountingPeriod: any;
    dashboard: any;
    declarationFilter: any;
    auditReport: any;
    notifications: any;
    accountableAccountTable: AccountableAccountTable;
    componentDictionary: any;
    autoVatItems: VatItemAuto[];
    assistVatItems: VatItemAssist[];
    previousVatCredit: PreviousVatCredit;
    importPlanData: any;
    invoiceMailTemplates: Array<MailTemplate>;
    selectedInvoiceMailTemplateName: string;

    tags: any;
}

const plugins = [];
if (globalThis.window?.sessionStorage) {
    plugins.push(
        createPersistedState({
            storage: globalThis.window?.sessionStorage,
        }),
    );
}

export const store = createStore<RootState>({
    plugins,
    state: {
        test: false,
        inAdmin: false,
        offerType: Offer_type.ACCOUNTING_PREMIUM,
        version: "1.0.0",
        maintenance: undefined,
        currentRoute: "",
        accountingPeriod: cloneDeep(defaultAccountingPeriod),
        accountableAccountTable: null,
        notifications: [],
        user: new User(),
        subUserId: null,
        selected_company: null,
        lastSelected_company: null,
        companyAccountingFirm: null,
        accountingFirmClient: null,
        // accountingFirmId: null,
        documentFilter: cloneDeep(defaultDocumentFilter),
        bankTransactionFilter: cloneDeep(defaultBankTransactionFilter),
        accountingLineFilter: {
            search: "",
            type: "ALL",
            allTypes: [],
            // status: 'ALL',
            filterByPrice: false,
            thirdPartyCompanyName: "",
            tags: [],
            amount: {
                maximum: "",
                minimum: "",
            },
            date: {
                begin: "",
                end: "",
                format: "YYYY-MM-DD",
            },
            // auditCheck: '',
            // partitionTagId: '',
            pagination: {
                sortBy: ["date"],
                descending: [true],
                page: 1,
                rowsPerPage: 25,
                totalItems: 0,
            },
            // prevNext: {
            //     currentId: "",
            //     prevId: "",
            //     nextId: ""
            // }
        },
        expenseReportsFilter: {
            search: "",
            amount: {
                maximum: "",
                minimum: "",
            },
            date: {
                begin: "",
                end: "",
            },
            status: "",
            pagination: {
                page: 1,
                sortBy: ["date"],
                descending: ["desc"],
                rowsPerPage: 40,
                totalItems: 0,
            },
        },
        ztillReceiptsFilter: {
            amount: {
                maximum: "",
                minimum: "",
            },
            date: {
                begin: "",
                end: "",
            },
            period: "",
            pagination: {
                page: 1,
                rowsPerPage: 10,
                totalItems: 0,
            },
        },
        companyFilter: {
            search: "",
            pagination: {
                sortBy: ["name"],
                descending: [false],
                page: 1,
                rowsPerPage: 10,
                totalItems: 0,
            },
        },
        companyEdition: {
            activeTab: "Company",
        },
        userFilter: {
            search: "",
            pagination: {
                sortBy: ["lastname"],
                descending: [false],
                page: 1,
                rowsPerPage: 20,
                totalItems: 0,
            },
        },
        invoiceFilter: {
            search: "",
            invoicePagination: {
                page: 1,
                sortBy: ["date"],
                descending: [false],
                rowsPerPage: 10,
                totalItems: 0,
            },
            quotationPagination: {
                page: 1,
                sortBy: ["date"],
                descending: [false],
                rowsPerPage: 10,
                totalItems: 0,
            },
            billDeliveryPagination: {
                page: 1,
                sortBy: ["date"],
                descending: [false],
                rowsPerPage: 10,
                totalItems: 0,
            },
            productPagination: {
                page: 1,
                sortBy: ["reference"],
                descending: [true],
                rowsPerPage: 10,
                totalItems: 0,
            },
            invoiceFilter: {
                search: "",
                type: "ALL",
                status: "ALL",
                clientCompaniesSelected: [],
                minAmount: "",
                maxAmount: "",
            },
            minAmount: "",
            maxAmount: "",
            status: "ALL",
            clientCompaniesSelected: [],
        },
        stockFilter: {
            search: "",
            operationPagination: {
                page: 1,
                sortBy: [{ key: "date", order: "asc" }],
                rowsPerPage: 10,
                totalItems: 0,
            },
            stockPagination: {
                page: 1,
                sortBy: [{ key: "reference", order: "desc" }],
                rowsPerPage: 10,
                totalItems: 0,
            },
            inventoryPagination: {
                page: 1,
                sortBy: [{ key: "date", order: "asc" }],
                rowsPerPage: 10,
                totalItems: 0,
            },
            productPagination: {
                page: 1,
                sortBy: [{ key: "reference", order: "desc" }],
                rowsPerPage: 10,
                totalItems: 0,
            },
        },
        auditFilter: {
            addEvenIfNone: false,
            allCompanies: false,
            isIgnored: false,
        },
        auditReport: {
            accountingReviewLayoutId: "default",
            accountingReviewLayout: [],
        },
        toBeDoneStatus: false,
        activateHelp: false,
        firstHelpDone: false,
        invoice: {
            activeTab: "Invoice",
            skipWizard: false,
        },
        stock: {
            activeTab: "Stock",
        },
        apolloErrors: {
            request: "",
            messages: [],
            type: "error",
        },
        generalLedger: {
            mode: "ACCOUNTING",
            expandedAccountableAccounts: [],
            filter: "ALL",
            bncMode: false,
            thirdParty: false,
            search: "",
            transactionType: "outcome",
            nm1: false,
            debitCredit: true,
            lastAccoutableAccountInteraction: "",
        },
        dashboard: {
            start: "",
            dateRange: [0, 11],
        },
        declarationFilter: {
            serach: "",
            pagination: {
                sortBy: ["declarationDate"],
                descending: [false],
                page: 1,
                rowsPerPage: 10,
                totalItems: 0,
            },
        },
        componentDictionary: {},
        autoVatItems: null,
        assistVatItems: null,
        previousVatCredit: null,
        importPlanData: null,
        invoiceMailTemplates: [],
        selectedInvoiceMailTemplateName: "default",

        tags: cloneDeep(defaultTag),
    },
    getters: {
        getCompany: (state) => (companyId) =>
            [...state.user.allAvailableCompanies, state.user.company].find(
                (c) => c.id === companyId,
            ),

        getAccountingPeriodFromDate: (state) => (date) =>
            state.accountingPeriod.accountingPeriods.find(
                (ap) => ap.start <= date && date <= ap.end,
            ),

        getAccountingPeriodFromId: (state) => (id) =>
            state.accountingPeriod.accountingPeriods.find((ap) => ap.id === id),

        getDocumentStartDate: (state) => () => {
            if (state && state.documentFilter.date.begin) {
                return moment(state.documentFilter.date.begin).startOf("day").format();
            }
            return undefined;
        },

        getDocumentEndDate: (state) => () => {
            if (state && state.documentFilter.date.end) {
                return moment(state.documentFilter.date.end).endOf("day").format();
            }
            return undefined;
        },

        isUserHasAccess: (state) => () => {
            if (state.offerType == Offer_type.ACCOUNTING_PREMIUM) {
                return true;
            } else if (state.user.profile.type === "ACCOUNTING_CLIENT") {
                return false;
            } else {
                return true;
            }
        },

        isAccountingUser: (state) => () => {
            return state.user.isAccountingUser;
        },
    },
    mutations: {
        updateCurrentRoute(state, data) {
            state.currentRoute = data;
            // state.accountingPeriod.isMandatory =
            //     apmRoutes?.indexOf(router.currentRoute.value.name as string) !== -1;
            state.accountingPeriod.isMandatory = apmRoutes?.indexOf(state.currentRoute) !== -1;
            state.accountingPeriod.readonly = false;

            if (!state.accountingPeriod.selectedAccountingPeriod) {
                //
                // mandatory
                //
                if (state.accountingPeriod.isMandatory) {
                    if (state.accountingPeriod.lastSelectedAccountingPeriod) {
                        state.accountingPeriod.selectedAccountingPeriod =
                            state.accountingPeriod.lastSelectedAccountingPeriod;
                        if (state.accountingPeriod.lastSelectedAccountingClosing) {
                            state.accountingPeriod.selectedAccountingClosing =
                                state.accountingPeriod.lastSelectedAccountingClosing;
                        }
                    } else if (state.accountingPeriod.accountingPeriods.length) {
                        state.accountingPeriod.selectedAccountingPeriod =
                            state.accountingPeriod.accountingPeriods[
                                state.accountingPeriod.accountingPeriods.length - 1
                            ];
                        if (state.accountingPeriod.selectedAccountingClosing) {
                            if (!state.accountingPeriod.selectedAccountingPeriod) {
                                state.accountingPeriod.selectedAccountingClosing = null;
                            } else if (
                                state.accountingPeriod.selectedAccountingPeriod.accountingClosings.findIndex(
                                    (ac) =>
                                        ac.id ===
                                        state.accountingPeriod.selectedAccountingClosing.id,
                                ) === -1
                            ) {
                                state.accountingPeriod.selectedAccountingClosing = null;
                            }
                        }
                    }
                }
            } else if (
                !state.accountingPeriod.isMandatory &&
                state.accountingPeriod.accountingPeriodWasNull
            ) {
                state.accountingPeriod.selectedAccountingPeriod = null;
                state.accountingPeriod.selectedAccountingClosing = null;
            }
        },
        setUser(state, user) {
            if (user) {
                if (!user.companyUser) {
                    user.companyUser = null;
                }
                if (user.companyUserOriginalProfile) {
                    user.companyUserOriginalProfile = null;
                }
            }

            state.user = Object.assign(new User(), user, {});
        },

        setCurrentCompany(state, company) {
            // if ((!state.selected_company && company) || (state.selected_company && !company) || (state.selected_company?.id !== company?.id)) {
            //     state.accountingPeriod = cloneDeep(defaultAccountingPeriod)
            // }
            state.selected_company = company;
            state.user.selectedCompany = company;

            state.generalLedger.bncMode = false;
            state.generalLedger.transactionType = "outcome";

            NotificationManager.upsertNotificationManager(state.selected_company?.id);
        },

        setCompanyAccountingFirm(state, companyAccountingFirm) {
            state.companyAccountingFirm = companyAccountingFirm;
        },

        setAccountingFirmClient(state, accountingFirmClient) {
            if (!state.companyAccountingFirm) {
                // state.offerType = null
                state.offerType = Offer_type.ACCOUNTING_PREMIUM;
                state.accountingFirmClient = null;
                return;
            }
            state.accountingFirmClient = accountingFirmClient;
            if (accountingFirmClient?.offer) {
                switch (accountingFirmClient.offer) {
                    case "ACCOUNTING_STARTER": {
                        state.offerType = Offer_type.ACCOUNTING_STARTER;
                        break;
                    }
                    case "ACCOUNTING_BASIC": {
                        state.offerType = Offer_type.ACCOUNTING_BASIC;
                        break;
                    }
                    case "ACCOUNTING_ESSENTIAL": {
                        state.offerType = Offer_type.ACCOUNTING_ESSENTIAL;
                        break;
                    }
                    case "ACCOUNTING_PREMIUM": {
                        state.offerType = Offer_type.ACCOUNTING_PREMIUM;
                        break;
                    }
                    case "BUSINESS_BASIC": {
                        state.offerType = Offer_type.BUSINESS_BASIC;
                        break;
                    }
                    case "BUSINESS_ESSENTIAL": {
                        state.offerType = Offer_type.BUSINESS_ESSENTIAL;
                        break;
                    }
                    case "BUSINESS_PREMIUM": {
                        state.offerType = Offer_type.BUSINESS_PREMIUM;
                        break;
                    }
                    case "BUSINESS_SMART": {
                        state.offerType = Offer_type.BUSINESS_SMART;
                        break;
                    }
                    case "TRAINING": {
                        state.offerType = Offer_type.TRAINING;
                        break;
                    }
                    default: {
                        state.offerType = Offer_type.TRAINING;
                    }
                }
            } else {
                state.offerType = Offer_type.ACCOUNTING_PREMIUM;
            }
        },

        setInternalManagement(state, status) {
            if (status) {
                state.inAdmin = true;
                if (state.selected_company) state.lastSelected_company = state.selected_company;
                state.selected_company = null;
            } else {
                state.inAdmin = false;
                if (!state.selected_company && state.lastSelected_company) {
                    state.selected_company = state.lastSelected_company;
                    state.lastSelected_company = null;
                }
            }
        },

        async beforeLogoutUser(state) {
            state.selected_company = null;
            state.user = new User();
            state.subUserId = null;
        },

        updateMaintenance(state, data) {
            state.maintenance = data;
        },

        updateOfferType(state, data) {
            state.offerType = data;
        },
        //
        // document
        //
        updateDocumentType(state, data) {
            state.documentFilter.type = data;
        },

        updateDocumentAllTypes(state, data) {
            state.documentFilter.allTypes = data;
        },

        updateDocumentStatus(state, data) {
            state.documentFilter.status = data;
        },

        updateDocumentDeclaredStatus(state, data) {
            state.documentFilter.declaredStatus = data;
        },

        updateDocumentPagination(state, data) {
            state.documentFilter.pagination = data;
        },

        updateDocumentShowFilterBox(state, data) {
            state.documentFilter.showFilterBox = data;
        },

        updateDocumentFilter(state, data) {
            state.documentFilter = data;
        },

        updateDocumentEndDate(state, data) {
            state.documentFilter.date.end = data;
        },

        updateDocumentBeginDate(state, data) {
            state.documentFilter.date.begin = data;
        },

        updateDocumentSearch(state, data) {
            state.documentFilter.search = data;
        },

        updateDocumentTags(state, data) {
            state.documentFilter.tags = data;
        },

        updateDocumentFilterByPrice(state, data) {
            state.documentFilter.filterByPrice = data;
        },

        updateDocumentThirdPartyCompanyName(state, data) {
            state.documentFilter.thirdPartyCompanyName = data;
        },

        updateDocumentMinAmount(state, data) {
            state.documentFilter.amount.minimum = data;
        },

        updateDocumentMaxAmount(state, data) {
            state.documentFilter.amount.maximum = data;
        },

        updateDocumentTotalItems(state, data) {
            state.documentFilter.pagination.totalItems = data;
        },

        updateDocumentPrevNext(state, data) {
            state.documentFilter.prevNext = data;
        },

        updateDocumentAuditCheck(state, data) {
            state.documentFilter.auditCheck = data;
        },

        updateDocumentPartitionTagId(state, data) {
            state.documentFilter.partitionTagId = data;
        },
        //
        // AccountingLine
        //
        updateAccountingLineType(state, data) {
            state.accountingLineFilter.type = data;
        },

        updateAccountingLineAllTypes(state, data) {
            state.accountingLineFilter.allTypes = data;
        },

        updateAccountingLineStatus(state, data) {
            state.accountingLineFilter.status = data;
        },

        updateAccountingLinePagination(state, data) {
            state.accountingLineFilter.pagination = data;
        },

        updateAccountingLineShowFilterBox(state, data) {
            state.accountingLineFilter.showFilterBox = data;
        },

        updateAccountingLineFilter(state, data) {
            state.accountingLineFilter = data;
        },

        updateAccountingLineEndDate(state, data) {
            state.accountingLineFilter.date.end = data;
        },

        updateAccountingLineBeginDate(state, data) {
            state.accountingLineFilter.date.begin = data;
        },

        updateAccountingLineSearch(state, data) {
            state.accountingLineFilter.search = data;
        },

        updateAccountingLineTags(state, data) {
            state.accountingLineFilter.tags = data;
        },

        updateAccountingLineFilterByPrice(state, data) {
            state.accountingLineFilter.filterByPrice = data;
        },

        updateAccountingLineThirdPartyCompanyName(state, data) {
            state.accountingLineFilter.thirdPartyCompanyName = data;
        },

        updateAccountingLineMinAmount(state, data) {
            state.accountingLineFilter.amount.minimum = data;
        },

        updateAccountingLineMaxAmount(state, data) {
            state.accountingLineFilter.amount.maximum = data;
        },

        updateAccountingLineTotalItems(state, data) {
            state.accountingLineFilter.pagination.totalItems = data;
        },
        // updateAccountingLinePrevNext(state, data) {
        //     state.accountingLineFilter.prevNext = data
        // },
        // updateAccountingLineAuditCheck(state, data) {
        //     state.accountingLineFilter.auditCheck = data
        // },
        // updateAccountingLinePartitionTagId(state, data) {
        //     state.accountingLineFilter.partitionTagId = data
        // },
        //
        // company
        //
        updateCompanyPagination(state, data) {
            state.companyFilter.pagination = data;
        },

        updateCompanySearch(state, data) {
            state.companyFilter.search = data;
        },

        updateUserPagination(state, data) {
            state.userFilter.pagination = data;
        },

        updateUserSearch(state, data) {
            state.userFilter.search = data;
        },

        updateBankTransactionAccount(state, data) {
            state.bankTransactionFilter.selectedAccount = data;
        },

        updateBankTransactionPayBy(state, data) {
            state.bankTransactionFilter.payBy = data;
        },

        updateBankTransactionAuditCheck(state, data) {
            state.bankTransactionFilter.auditCheck = data;
        },

        updateBankTransactionPartitionTagId(state, data) {
            state.bankTransactionFilter.partitionTagId = data;
        },

        updateBankTransactionPagination(state, data) {
            state.bankTransactionFilter.pagination = data;
        },

        updateBankTransactionShowFilterBox(state, data) {
            state.bankTransactionFilter.showFilterBox = data;
        },

        updateBankTransactionFilter(state, data) {
            state.bankTransactionFilter = data;
        },

        updateBankTransactionEndDate(state, data) {
            state.bankTransactionFilter.date.end = data;
        },

        updateBankTransactionBeginDate(state, data) {
            state.bankTransactionFilter.date.begin = data;
        },

        updateBankTransactionSearch(state, data) {
            state.bankTransactionFilter.search = data;
        },

        updateBankTransactionAmountType(state, data) {
            state.bankTransactionFilter.amount.type = data;
        },

        updateBankTransactionMinAmount(state, data) {
            state.bankTransactionFilter.amount.minimum = data;
        },

        updateBankTransactionMaxAmount(state, data) {
            state.bankTransactionFilter.amount.maximum = data;
        },
        // updateBankTransactionShowNonReconcilied(state, data) {
        //     state.bankTransactionFilter.showNonReconcilied = data
        // },
        updateBankTransactionReconciliationStatus(state, data) {
            state.bankTransactionFilter.reconciliationStatus = data;
        },

        updateBankTransactionPrevNext(state, data) {
            state.bankTransactionFilter.prevNext = data;
        },

        updateBankTransactionDisplayGraph(state, data) {
            state.bankTransactionFilter.displayGraph = data;
        },

        updateBankTransactionTags(state, data) {
            state.bankTransactionFilter.tags = data;
        },

        updateToBeDoneStatus(state, data) {
            state.toBeDoneStatus = data;
        },

        updateNotifications(state, data) {
            state.notifications = data;
        },

        updateActivateHelp(state, data) {
            state.activateHelp = data;
        },

        updateFirstHelpDone(state, data) {
            state.firstHelpDone = data;
        },

        updateInvoiceActiveTab(state, data) {
            state.invoice.activeTab = data;
        },

        updateStockActiveTab(state, data) {
            state.stock.activeTab = data;
        },

        updateExpenseReportsSearch(state, data) {
            state.expenseReportsFilter.search = data;
        },

        updateExpenseReportsBeginDate(state, data) {
            state.expenseReportsFilter.date.begin = data;
        },

        updateExpenseReportsEndDate(state, data) {
            state.expenseReportsFilter.date.end = data;
        },

        updateExpenseReportsMinAmount(state, data) {
            state.expenseReportsFilter.amount.minimum = data;
        },

        updateExpenseReportsMaxAmount(state, data) {
            state.expenseReportsFilter.amount.maximum = data;
        },

        updateExpenseReportsStatus(state, data) {
            state.expenseReportsFilter.status = data;
        },

        updateExpenseReportsPagination(state, data) {
            state.expenseReportsFilter.pagination = data;
        },

        updateZtillReceiptsBeginDate(state, data) {
            state.ztillReceiptsFilter.date.begin = data;
        },

        updateZtillReceiptsEndDate(state, data) {
            state.ztillReceiptsFilter.date.end = data;
        },

        updateZtillReceiptsMinAmount(state, data) {
            state.ztillReceiptsFilter.amount.minimum = data;
        },

        updateZtillReceiptsMaxAmount(state, data) {
            state.ztillReceiptsFilter.amount.maximum = data;
        },

        updateZtillReceiptsPeriod(state, data) {
            state.ztillReceiptsFilter.period = data;
        },

        upsertCompanyEditionActiveTab(state, data) {
            state.companyEdition.activeTab = data;
        },

        setApolloErrors(state, data) {
            if (!data.type || data.type !== "") {
                data.type = "error";
            }
            state.apolloErrors = data;
        },

        updateZtillReceiptsPagination(state, data) {
            state.ztillReceiptsFilter.pagination = data;
        },
        //
        // Invoice
        //
        updateInvoiceMinAmount(state, data) {
            state.invoiceFilter.minAmount = data;
        },

        updateInvoiceMaxAmount(state, data) {
            state.invoiceFilter.maxAmount = data;
        },

        updateInvoiceStatus(state, data) {
            state.invoiceFilter.status = data;
        },

        updateInvoiceSearch(state, data) {
            state.invoiceFilter.search = data;
        },

        updateInvoiceClientCompaniesSelected(state, data) {
            state.invoiceFilter.clientCompaniesSelected = data;
        },

        updateQuotationPagination(state, data) {
            state.invoiceFilter.quotationPagination = data;
        },

        updateInvoicePagination(state, data) {
            state.invoiceFilter.invoicePagination = data;
        },

        updateBillDeliveryPagination(state, data) {
            state.invoiceFilter.billDeliveryPagination = data;
        },

        updateProductPagination(state, data) {
            state.invoiceFilter.productPagination = data;
        },

        updateInvoiceFilter(state, data) {
            state.invoiceFilter = data;
        },
        //
        // Stock
        //
        updateStockSearch(state, data) {
            state.stockFilter.search = data;
        },

        updateStockClientCompaniesSelected(state, data) {
            state.stockFilter.clientCompaniesSelected = data;
        },

        updateStockOperationPagination(state, data) {
            state.stockFilter.operationPagination = data;
        },

        updateStockProductPagination(state, data) {
            state.stockFilter.productPagination = data;
        },
        //
        // GeneralLedger
        //
        updateExpandedAccountableAccountsGeneralLedger(state, data) {
            state.generalLedger.expandedAccountableAccounts = data;
        },

        updateBNCModeGeneralLedger(state, data) {
            state.generalLedger.bncMode = data;
        },

        updateFilterGeneralLedger(state, data) {
            state.generalLedger.filter = data;
        },

        updateThirdPartyGeneralLedger(state, data) {
            state.generalLedger.thirdParty = data;
        },

        updateSearchGeneralLedger(state, data) {
            state.generalLedger.search = data;
        },

        updateTransactionTypeGeneralLedger(state, data) {
            state.generalLedger.transactionType = data;
        },

        updateModeGeneralLedger(state, data) {
            state.generalLedger.mode = data;
        },

        updateNm1GeneralLedger(state, data) {
            state.generalLedger.nm1 = data;
        },

        updateDebitCreditGeneralLedger(state, data) {
            state.generalLedger.debitCredit = data;
        },

        updateAuditFilterAddEvenIfNone(state, data) {
            state.auditFilter.addEvenIfNone = data;
        },

        updateAuditFilterAllCompanies(state, data) {
            state.auditFilter.allCompanies = data;
        },

        updateAuditFilterIsIgnored(state, data) {
            state.auditFilter.isIgnored = data;
        },

        //
        // accounringPeriods
        //
        updateAccountingPeriods(state, data) {
            state.accountingPeriod.accountingPeriods = JSON.parse(JSON.stringify(data));
            for (const ap of state.accountingPeriod.accountingPeriods) {
                ap.start = moment(ap.start).startOf("day").format();
                ap.end = moment(ap.end).endOf("day").format();
            }
            const previousSelectedAccountingPeriod =
                state.accountingPeriod.selectedAccountingPeriod;
            const previousLastSelectedAccountingPeriod =
                state.accountingPeriod.lastSelectedAccountingPeriod;
            const previousLastSelectedAccountingClosing =
                state.accountingPeriod.lastSelectedAccountingClosing;
            state.accountingPeriod.selectedAccountingPeriod = null;
            state.accountingPeriod.selectedAccountingClosing = null;
            state.accountingPeriod.lastSelectedAccountingPeriod = null;
            if (state.accountingPeriod.accountingPeriods.length) {
                if (previousSelectedAccountingPeriod) {
                    const foundAP = state.accountingPeriod.accountingPeriods.find(
                        (ap) => ap.id === previousSelectedAccountingPeriod.id,
                    );
                    if (foundAP) {
                        state.accountingPeriod.lastSelectedAccountingPeriod = foundAP;
                        state.accountingPeriod.selectedAccountingPeriod = foundAP;
                    }
                } else {
                    const foundAP = previousLastSelectedAccountingPeriod
                        ? state.accountingPeriod.accountingPeriods.find(
                              (ap) => ap.id === previousLastSelectedAccountingPeriod.id,
                          )
                        : null;
                    if (foundAP) {
                        state.accountingPeriod.lastSelectedAccountingPeriod = foundAP;
                    } else {
                        state.accountingPeriod.lastSelectedAccountingPeriod =
                            state.accountingPeriod.accountingPeriods[
                                state.accountingPeriod.accountingPeriods.length - 1
                            ];
                    }
                    if (state.accountingPeriod.isMandatory) {
                        state.accountingPeriod.selectedAccountingPeriod =
                            state.accountingPeriod.lastSelectedAccountingPeriod;
                    }
                }
                if (
                    state.accountingPeriod.selectedAccountingPeriod?.accountingClosings &&
                    previousLastSelectedAccountingClosing
                ) {
                    if (
                        state.accountingPeriod.selectedAccountingPeriod.accountingClosings.find(
                            (ac) => ac.id === previousLastSelectedAccountingClosing.id,
                        )
                    ) {
                        state.accountingPeriod.selectedAccountingClosing =
                            previousLastSelectedAccountingClosing;
                    }
                }
            }
        },

        updateLastAccoutableAccountInteraction(state, data) {
            state.generalLedger.lastAccoutableAccountInteraction = data;
        },

        updateSelectedAccountingPeriod(state, data) {
            state.accountingPeriod.selectedAccountingPeriod = data;
            if (
                state.accountingPeriod.selectedAccountingClosing &&
                state.accountingPeriod.selectedAccountingPeriod?.accountingClosings?.findIndex(
                    (ac) => ac.id === state.accountingPeriod.selectedAccountingClosing.id,
                ) === -1
            ) {
                state.accountingPeriod.selectedAccountingClosing = null;
            }
            if (data) {
                state.accountingPeriod.lastSelectedAccountingPeriod = data;
                if (!state.accountingPeriod.isMandatory) {
                    state.accountingPeriod.accountingPeriodWasNull = false;
                }
            } else {
                state.accountingPeriod.accountingPeriodWasNull = true;
            }
        },

        updateAccountingPeriodReadOnly(state, data) {
            state.accountingPeriod.readonly = data;
        },

        updateLastSelectedAccountingPeriod(state, data) {
            state.accountingPeriod.lastSelectedAccountingPeriod = data;
        },
        //
        // accountingClosing
        //
        updateSelectedAccountingClosing(state, data) {
            state.accountingPeriod.selectedAccountingClosing = data;
            if (data) {
                state.accountingPeriod.lastSelectedAccountingClosing = data;
                // if (!state.accountingPeriod.isMandatory) {
                //     state.accountingPeriod.accountingPeriodWasNull = false
                // }
            } else {
                // state.accountingPeriod.accountingPeriodWasNull = true
            }
        },
        //
        // dashboard
        //
        updateDashboardStart(state, data) {
            state.dashboard.start = data;
        },

        updateDashboardDateRange(state, data) {
            if (data && data[0] === -1) return;
            state.dashboard.dateRange = data;
        },

        clearDocumentFilter(state) {
            state.documentFilter = cloneDeep(defaultDocumentFilter);
        },

        clearBankTransactionFilter(state) {
            state.bankTransactionFilter = cloneDeep(defaultBankTransactionFilter);
        },
        //
        // Declaration
        //
        updateDeclarationPagination(state, data) {
            state.declarationFilter = data;
        },
        //
        // Accounting Review Layout
        //
        updateAuditReportAccountingReviewLayoutId(state, data) {
            state.auditReport.accountingReviewLayoutId = data;
        },

        updateAuditReportAccountingReviewLayout(state, data) {
            state.auditReport.accountingReviewLayout = data;
        },

        updateAuditReportPreviousNextFilteredAccountingReviewLayout(state, data) {
            state.auditReport.previousNextFilteredControls = data;
        },

        //
        // cognito
        //
        //
        // updateSubUserId
        //
        updateSubUserId(state, data) {
            state.subUserId = data;
        },

        updateTest(state, data) {
            state.test = data;
        },

        updateAccountableAccountTable(state, data) {
            state.accountableAccountTable = cloneDeep(data);
            // state.accountableAccountTable = data
        },

        "invoice/skip-wizard"(state, data) {
            state.invoice.skipWizard = data;
        },

        updateComponentDictionary(state, dictionary) {
            // New mutation
            state.componentDictionary = dictionary;
        },

        updateAutoVatItems(state, autoVatItems) {
            state.autoVatItems = autoVatItems;
        },
        updateAssistVatItems(state, assistVatItems) {
            state.assistVatItems = assistVatItems;
        },
        updatePreviousVatCredit(state, previousVatCredit) {
            state.previousVatCredit = previousVatCredit;
        },
        resetVatItems(state) {
            state.autoVatItems = null;
            state.assistVatItems = null;
            state.previousVatCredit = null;
        },

        //
        // updateAccountingFirmId
        //
        // updateAccountingFirmId(state, data) {
        //     state.accountingFirmId = data
        // }

        //
        // updateCurrentProfile
        //
        updateCurrentProfile(state, newProfile) {
            // Sauvegarde du profil original si ce n'est pas déjà fait
            if (!state.user.switchOriginalProfile) {
                state.user.switchOriginalProfile = state.user.profile;
            }
            // Mise à jour du profil actuel avec le nouveau profil sélectionné
            state.user.profile = newProfile;
        },
        resetProfileToOriginal(state) {
            // Restauration du profil original
            if (
                state.user.switchOriginalProfile &&
                state.user.profile &&
                state.user.profile.id !== state.user.switchOriginalProfile.id
            ) {
                state.user.profile = state.user.switchOriginalProfile;
                state.user.switchOriginalProfile = null;
            }
        },
        updateImportPlanData(state, data) {
            state.importPlanData = data;
        },
        setInvoiceMailTemplates(state, templates: Array<MailTemplate>) {
            state.invoiceMailTemplates = templates;
            localStorage.setItem("invoiceMailTemplates", JSON.stringify(templates));
        },
        addInvoiceMailTemplate(state, template: MailTemplate) {
            state.invoiceMailTemplates = [...state.invoiceMailTemplates, { ...template }];
            localStorage.setItem(
                "invoiceMailTemplates",
                JSON.stringify(state.invoiceMailTemplates),
            );
        },
        updateInvoiceMailTemplate(state, templateToUpdate: MailTemplate) {
            const index = state.invoiceMailTemplates.findIndex(
                (template) => template.value === templateToUpdate.value,
            );
            if (index !== -1) {
                state.invoiceMailTemplates.splice(index, 1, { ...templateToUpdate });
            } else {
                console.error("Template not found");
            }
        },
        setSelectedTemplateName(state, templateName: string) {
            state.selectedInvoiceMailTemplateName = templateName;
        },
        deleteInvoiceMailTemplate(state, templatename: string) {
            state.invoiceMailTemplates = state.invoiceMailTemplates.filter(
                (template) => template.value !== templatename,
            );

            localStorage.setItem(
                "invoiceMailTemplates",
                JSON.stringify(state.invoiceMailTemplates),
            );
        },
        //
        // tag
        //
        updateTagExpandPartitions(state, data) {
            state.tags.expandPartitions = data;
        },
    } as MutationTree<RootState>,
    actions: {
        async changeProfile({ commit, state }, newProfile) {
            if (newProfile && newProfile.id !== state.user.profile.id) {
                commit("updateCurrentProfile", newProfile);
            }
        },

        resetProfile({ commit }) {
            commit("resetProfileToOriginal");
        },
        // Action to initialize mail templates
        initializeInvoiceMailTemplates({ commit }, defaultTemplate: MailTemplate) {
            commit("setInvoiceMailTemplates", [defaultTemplate]);
        },

        // Action to add a new mail template
        addInvoiceMailTemplate({ commit }, newTemplate: MailTemplate) {
            commit("addInvoiceMailTemplate", newTemplate);
        },
        updateInvoiceMailTemplate({ commit }, templateToUpdate: MailTemplate) {
            commit("updateInvoiceMailTemplate", templateToUpdate);
        },
        // Action to update the selected mail template
        updateSelectedTemplateName({ commit }, templateName: string) {
            commit("setSelectedTemplateName", templateName);
        },
        deleteInvoiceMailTemplate({ commit }, templateName: string) {
            commit("deleteInvoiceMailTemplate", templateName);
        },
    } as ActionTree<RootState, RootState>,
});

window.addEventListener("storage", (event) => {
    if (event.key === "invoiceMailTemplates") {
        try {
            const updatedTemplates = JSON.parse(event.newValue || "[]");
            store.commit("setInvoiceMailTemplates", updatedTemplates);
        } catch (error) {
            console.error("Erreur de synchronisation des modèles d'email :", error);
        }
    }
});
